import { Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Select } from '~/components/form';
import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  type DataTableColumn,
  FilterDrawer,
  Pagination,
  RowActionsMenu,
  StatusChip,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { errorText } from '~/lib/apiError';
import { clampPage, pageCount } from '~/lib/listParams';
import {
  CHANNEL_LABELS,
  matchesProviderKeyFilters,
  PROVIDER_CREDENTIAL_KEYS,
  providerKeyTitle,
  providerLabel,
} from '~/lib/notification/providerKeyForm';
import {
  useDeleteProviderKeyMutation,
  useListProviderKeysQuery,
} from '~/modules/query/notification/providerKeys';
import type { ProviderKey } from '~/modules/query/notification/types';
import { routes } from '~/app/routes';

const FILTER_KEYS = ['channel', 'provider', 'active'] as const;

const CHANNEL_OPTIONS = Object.entries(CHANNEL_LABELS).map(([value, label]) => ({ value, label }));
const ACTIVE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const dash = <span className="text-gray-400 dark:text-gray-500">—</span>;

export default function ProviderKeysPage() {
  const list = useListUrlState({ filterKeys: FILTER_KEYS });
  const keys = useListProviderKeysQuery();
  const [deleteProviderKey] = useDeleteProviderKeyMutation();
  const [showFilters, setShowFilters] = useState(false);
  const [deleting, setDeleting] = useState<ProviderKey | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const all = useMemo(() => keys.data ?? [], [keys.data]);

  const providerOptions = useMemo(
    () =>
      [
        ...new Set([...Object.keys(PROVIDER_CREDENTIAL_KEYS), ...all.map((key) => key.provider)]),
      ].map((value) => ({ value, label: providerLabel(value) })),
    [all],
  );

  const rows = useMemo(
    () =>
      all.filter((key) =>
        matchesProviderKeyFilters(key, {
          q: list.q,
          channel: list.filters.channel,
          provider: list.filters.provider,
          active: list.filters.active,
        }),
      ),
    [all, list.q, list.filters],
  );

  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const pageRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);
  const filtered = Boolean(list.q) || list.filterCount > 0;

  const remove = async () => {
    if (!deleting) return;
    setActionError(null);
    try {
      await deleteProviderKey(deleting.id).unwrap();
      toast.success('Provider key deleted');
    } catch (error) {
      setActionError(errorText(error, 'The provider key could not be deleted. Try again.'));
    } finally {
      setDeleting(null);
    }
  };

  // Credentials are deliberately not a column: the list never renders them, masked or not.
  const columns: DataTableColumn<ProviderKey>[] = [
    {
      id: 'provider',
      header: 'Provider',
      cell: (key) => (
        <p className="font-medium text-gray-800 dark:text-white/90">
          {providerLabel(key.provider)}
        </p>
      ),
    },
    {
      id: 'channel',
      header: 'Channel',
      cell: (key) => CHANNEL_LABELS[key.channel] ?? key.channel,
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (key) =>
        key.customerId ? (
          <span className="font-mono text-xs">{key.customerId}</span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">All customers</span>
        ),
    },
    {
      id: 'routing',
      header: 'Channel key',
      cell: (key) =>
        key.isDefault ? (
          <StatusChip label="Default" tone="brand" />
        ) : key.channelKey ? (
          <span className="font-mono text-xs">{key.channelKey}</span>
        ) : (
          dash
        ),
    },
    {
      id: 'defaultFrom',
      header: 'Default from',
      cell: (key) =>
        key.defaultFrom ? <span className="font-mono text-xs">{key.defaultFrom}</span> : dash,
    },
    { id: 'priority', header: 'Priority', cell: (key) => key.priority },
    {
      id: 'active',
      header: 'Status',
      cell: (key) => (
        <StatusChip
          label={key.isActive ? 'Active' : 'Inactive'}
          tone={key.isActive ? 'success' : 'neutral'}
          dot
        />
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (key) => (
        <RowActionsMenu
          testId={`provider-keys-actions-${key.id}`}
          ariaLabel={`Open actions for ${providerKeyTitle(key)}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.notificationProviderKey(key.id),
            },
            {
              label: 'Delete',
              testId: 'delete',
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              destructive: true,
              onClick: () => setDeleting(key),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageShell>
        <PageHeader
          title="Provider keys"
          description="The email and SMS providers that deliver notifications, and which messages use each."
          breadcrumbs={[{ label: 'Notifications' }, { label: 'Provider keys' }]}
          actions={[
            {
              label: 'Create provider key',
              kind: 'primary',
              href: routes.notificationProviderKeyNew,
              testId: 'provider-keys-new-button',
            },
          ]}
        />

        {actionError && <Alert variant="error" message={actionError} />}

        <Card>
          <ListToolbar
            title="Provider keys"
            search={{
              value: list.q,
              onChange: list.setQ,
              placeholder: 'Search provider keys…',
              debounceMs: 0,
              testId: 'provider-keys-search',
            }}
            filters={{
              count: list.filterCount,
              onClick: () => setShowFilters(true),
              onClear: list.clear,
              testId: 'provider-keys-filter-button',
            }}
          />
          <CardBody className="p-0">
            <DataTable<ProviderKey>
              framed={false}
              testId="provider-keys-table"
              loading={!keys.data && !keys.error}
              refreshing={keys.isFetching && !!keys.data}
              data={pageRows}
              rowKey={(key) => key.id}
              href={(key) => routes.notificationProviderKey(key.id)}
              rowLabel={(key) => `Open ${providerKeyTitle(key)}`}
              columns={columns}
              error={
                keys.error && !keys.data
                  ? {
                      what: 'provider keys',
                      message: errorText(keys.error, 'The provider keys could not be loaded.'),
                      onRetry: () => keys.refetch(),
                    }
                  : undefined
              }
              emptyMessage={filtered ? 'No provider keys match.' : 'No provider keys yet.'}
              emptyDescription={
                filtered
                  ? 'Clear the search or filters to see every key.'
                  : 'Create a provider key so email and SMS notifications can be delivered.'
              }
            />
          </CardBody>
          <Pagination
            pageIndex={page - 1}
            pageSize={list.pageSize}
            pageCount={pages}
            totalRows={rows.length}
            onPageChange={(index) => list.setPage(index + 1)}
            pageSizeControl={{
              value: list.pageSize,
              onChange: list.setPageSize,
              options: [...list.pageSizes],
            }}
          />
        </Card>
      </PageShell>

      <FilterDrawer<Record<string, string>>
        open={showFilters}
        onClose={() => setShowFilters(false)}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        testId="provider-keys-filter-drawer"
      >
        {(draft, patch) => (
          <>
            <Select
              id="provider-keys-filter-channel"
              label="Channel"
              placeholder="All channels"
              placeholderSelectable
              options={CHANNEL_OPTIONS}
              value={draft.channel}
              onChange={(channel) => patch({ channel })}
            />
            <Select
              id="provider-keys-filter-provider"
              label="Provider"
              placeholder="All providers"
              placeholderSelectable
              options={providerOptions}
              value={draft.provider}
              onChange={(provider) => patch({ provider })}
            />
            <Select
              id="provider-keys-filter-active"
              label="Status"
              placeholder="Active and inactive"
              placeholderSelectable
              options={ACTIVE_OPTIONS}
              value={draft.active}
              onChange={(active) => patch({ active })}
            />
          </>
        )}
      </FilterDrawer>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete provider key"
        message={`Delete the ${deleting ? providerKeyTitle(deleting) : ''} key?`}
        confirmLabel="Delete provider key"
        variant="danger"
        dangerNote="Messages that rely on this key stop sending on this channel. This cannot be undone."
        testId="provider-keys-delete"
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      />
    </>
  );
}
